import React, { useState, useEffect } from 'react';
import Heading from '../../Components/Heading/heading.tsx';
import '../../Components/Button/button-style.scss';
import { 
  Dialog, 
  DialogContent, 
  IconButton, 
  Tooltip, 
  Box, 
  Grid, 
  Typography 
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import DocumentUploadDialog from '../../Components/DocumentUploadDialog/document-upload-dialog.tsx';
import ConfirmationPopup from '../../Components/ConfirmationModal/confirmation-modal.tsx';
import { addDocuments, deleteDocument, editDocuments } from './action.ts';
import { toast } from 'react-toastify';

interface AddDocumentModalProps {
  isDocumentOpen: boolean;
  vehicleId: number;
  handleClose: () => void;  
}

interface UploadedFile {
  location: string;
  id: number;
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ isDocumentOpen, handleClose, vehicleId }) => {
  const maxFileSize = 5 * 1024 * 1024; 
  const allowedFileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
  
  const [insuranceExpiryDate, setInsuranceExpiryDate] = useState<string>('');
  const [fitnessExpiryDate, setFitnessExpiryDate] = useState<string>('');
  const [registrationDate, setRegistrationDate] = useState<string>('');
  const [pollutionDate, setPollutionDate] = useState<string>('');
  
  const [initialInsuranceDate, setInitialInsuranceDate] = useState<any>('');
  const [initialFitnessDate, setInitialFitnessDate] = useState<any>('');
  
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File | null }>({
    registrationCertificate: null,
    pollutionCertificate: null,
    fitnessCertificate: null,
    insurance: null,
    drivers_license: null,
    aadhar: null
  });
  
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: UploadedFile | null }>({
    registrationCertificate: null,
    pollutionCertificate: null,
    fitnessCertificate: null,
    insurance: null,
    drivers_license: null,
    aadhar: null
  });
  
  const [icons, setIcons] = useState<{ [key: string]: string }>({
    registrationCertificate: 'upload',
    pollutionCertificate: 'upload',
    fitnessCertificate: 'upload',
    insurance: 'upload'
  });

  const [uploadFlow, setUploadFlow] = useState({
    isOpen: false,
    docType: '',
    selectedDate: ''
  });

  const resetState = () => {
    setSelectedFiles({
      registrationCertificate: null,
      pollutionCertificate: null,
      fitnessCertificate: null,
      insurance: null,
      drivers_license: null,
      aadhar: null,
    });
    setUploadedFiles({
      registrationCertificate: null,
      pollutionCertificate: null,
      fitnessCertificate: null,
      insurance: null,
      drivers_license: null,
      aadhar: null,
    });
    setInsuranceExpiryDate('');
    setFitnessExpiryDate('');
    setRegistrationDate('');
    setPollutionDate('');
    setIcons({
      registrationCertificate: 'upload',
      pollutionCertificate: 'upload',
      fitnessCertificate: 'upload',
      insurance: 'upload',
    });
    setUploadFlow({ isOpen: false, docType: '', selectedDate: '' });
  };

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [docIdToDelete, setDocIdToDelete] = useState(0);
  const [docTypeToDelete, setDocTypeToDelete] = useState<string | null>(null);

  const handlePopUpClose = () => {
    resetState();
    handleClose();
  };

  const handleOpenDeleteModal = (id: number, type: string) => {
    setDocIdToDelete(id);
    setDocTypeToDelete(type);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setDocIdToDelete(0);
    setDocTypeToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (docIdToDelete && docTypeToDelete) {
      deleteDocument(
        docIdToDelete, 
        vehicleId, 
        (response) => {
          toast.success(`${docTypeToDelete.replace('_', ' ')} removed successfully`);
          setUploadedFiles((prevFiles) => ({
            ...prevFiles,
            [docTypeToDelete]: null
          }));
          setSelectedFiles((prevFiles) => ({
            ...prevFiles,
            [docTypeToDelete]: null
          }));
          setIcons((prevIcons) => ({
            ...prevIcons,
            [docTypeToDelete]: 'upload'
          }));
          
          if (docTypeToDelete === 'insurance') setInsuranceExpiryDate('');
          if (docTypeToDelete === 'fitnessCertificate') setFitnessExpiryDate('');
          if (docTypeToDelete === 'registrationCertificate') setRegistrationDate('');
          if (docTypeToDelete === 'pollutionCertificate') setPollutionDate('');
          
          handleCloseDeleteModal();
        },
        (errorMessage) => {
          toast.error(`Error removing ${docTypeToDelete?.replace('_', ' ')}`);
          handleCloseDeleteModal();
        }
      );
    }
  };

  const uploadFile = (
    type: string, 
    file: File, 
    fitnessDate: string, 
    insuranceDate: string, 
    regDate: string, 
    pollDate: string, 
    action: string
  ) => {
    if (file) {
      if (action === 'add') {
        addDocuments(
          type === 'drivers_license' ? file : null,
          type === 'aadhar' ? file : null,
          type === 'registrationCertificate' ? file : null,
          type === 'insurance' ? file : null,
          type === 'fitnessCertificate' ? file : null,
          type === 'pollutionCertificate' ? file : null,
          vehicleId,
          fitnessDate,
          insuranceDate,
          regDate,
          pollDate,
          (response) => {
            setUploadedFiles((prevFiles) => ({
              ...prevFiles,
              [type]: { location: response.message.location, id: response.message.id }
            }));
            toast.success(`${type.replace('_', ' ')} uploaded successfully`);
          },
          (errMessage: any) => {  
            if (errMessage && errMessage.status === 400) {
              let allErrorMessages: string[] = [];
              if (typeof errMessage.message === 'string') {
                allErrorMessages.push(errMessage.message);
                setSelectedFiles((prevFiles) => ({
                  ...prevFiles,
                  [type]: file
                }));
                setIcons((prevIcons) => ({
                  ...prevIcons,
                  [type]: 'view'
                }));
                toast.error(allErrorMessages.join(" "));
              } else if (errMessage.message && typeof errMessage.message === 'object') {
                const errors = errMessage.message.errors || {};
                for (const [key, messages] of Object.entries(errors)) {
                  if (Array.isArray(messages as any)) {
                    allErrorMessages.push(...(messages as any));
                  }
                }
                setSelectedFiles((prevFiles) => ({
                  ...prevFiles,
                  [type]: null
                }));
                setIcons((prevIcons) => ({
                  ...prevIcons,
                  [type]: 'upload'
                }));
                toast.error(allErrorMessages.join(" "));
              }
            } else {
              toast.error("An unexpected error occurred.");
            }
          }
        );
      } else {
        editDocuments(
          type === 'drivers_license' ? file : null,
          type === 'aadhar' ? file : null,
          type === 'registrationCertificate' ? file : null,
          type === 'insurance' ? file : null,
          type === 'fitnessCertificate' ? file : null,
          type === 'pollutionCertificate' ? file : null,
          vehicleId,
          fitnessDate,
          insuranceDate,
          regDate,
          pollDate,
          (response) => {
            if (response.status === 200) {           
              setSelectedFiles((prevFiles) => ({
                ...prevFiles,
                [type]: file                
              }));
              setIcons((prevIcons) => ({
                ...prevIcons,
                [type]: 'view'
              }));
              toast.success("Document Uploaded Successfully");
            }              
          },
          (errMessage: any) => {  
            let allErrorMessages: string[] = [];
            if (errMessage.status === 400) {
              if (typeof errMessage.message === 'string') {
                allErrorMessages.push(errMessage.message);
                setSelectedFiles((prevFiles) => ({
                  ...prevFiles,
                  [type]: file
                }));
                setIcons((prevIcons) => ({
                  ...prevIcons,
                  [type]: 'view'
                }));
                toast.error(allErrorMessages.join(" "));
              } else {
                toast.error(errMessage.message);
              }
            } else if (errMessage.status === 413) {
              toast.error("Document Size is large");
            } else if (errMessage.message && typeof errMessage.message === 'object') {
              const errors = errMessage.message.errors || {};
              for (const [key, messages] of Object.entries(errors)) {
                if (Array.isArray(messages as any)) {
                  allErrorMessages.push(...(messages as any));
                }
              }
              setSelectedFiles((prevFiles) => ({
                ...prevFiles,
                [type]: null
              }));
              setIcons((prevIcons) => ({
                ...prevIcons,
                [type]: 'upload'
              }));
              toast.error(allErrorMessages.join(" "));
            }
          } 
        );
      }
    }
  };

  useEffect(() => {
    console.log(vehicleId);
  }, [vehicleId]);

  const triggerUploadFlow = (type: string) => {
    setUploadFlow({ isOpen: true, docType: type, selectedDate: '' });
  };

  const handleDateSelected = (date: string, docType: string) => {
    setUploadFlow(prev => ({ ...prev, isOpen: false, selectedDate: date }));
    setTimeout(() => {
      const fileInput = document.getElementById(`${docType}-file`) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }, 150); 
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const fileInput = event.target; 
    const file = fileInput.files?.[0] || null;
    const maxFileSize = 5 * 1024 * 1024;
    
    if (!file) {
      return;
    }

    if (!allowedFileTypes.includes(file.type)) {
      toast.error("File type not supported");
      fileInput.value = '';
      return;
    }

    if (file.size > maxFileSize) {
      toast.error("File size must not exceed 5 MB");
      fileInput.value = '';
      return;
    }

    setSelectedFiles((prevFiles) => ({
      ...prevFiles,
      [type]: file
    }));
    
    setIcons((prevIcons) => ({
      ...prevIcons,
      [type]: 'view'
    }));

    if (vehicleId && file) {
      const flowDate = uploadFlow.selectedDate;

      if (type === 'fitnessCertificate' && !flowDate && !fitnessExpiryDate) {
        toast.error('Expiry date is required for Fitness Certificate');
        setIcons((prevIcons) => ({ ...prevIcons, [type]: 'upload' }));
      } else if (type === 'insurance' && !flowDate && !insuranceExpiryDate) {
        toast.error('Expiry date is required for Insurance Certificate');
        setIcons((prevIcons) => ({ ...prevIcons, [type]: 'upload' }));
      } else if (type === 'registrationCertificate' && !flowDate && !registrationDate) {
        toast.error('Date is required for Registration Certificate');
        setIcons((prevIcons) => ({ ...prevIcons, [type]: 'upload' }));
      } else if (type === 'pollutionCertificate' && !flowDate && !pollutionDate) {
        toast.error('Date is required for Pollution Certificate');
        setIcons((prevIcons) => ({ ...prevIcons, [type]: 'upload' }));
      } else {
        const fitDate = type === 'fitnessCertificate' ? flowDate : fitnessExpiryDate;
        const insDate = type === 'insurance' ? flowDate : insuranceExpiryDate;
        const regDate = type === 'registrationCertificate' ? flowDate : registrationDate;
        const pollDate = type === 'pollutionCertificate' ? flowDate : pollutionDate;

        if (type === 'fitnessCertificate' && flowDate) setFitnessExpiryDate(flowDate);
        if (type === 'insurance' && flowDate) setInsuranceExpiryDate(flowDate);
        if (type === 'registrationCertificate' && flowDate) setRegistrationDate(flowDate);
        if (type === 'pollutionCertificate' && flowDate) setPollutionDate(flowDate);

        uploadFile(type, file, fitDate, insDate, regDate, pollDate, 'add');
      }
    }
    
    fileInput.value = '';
    setUploadFlow({ isOpen: false, docType: '', selectedDate: '' });
  };

  const handleIconClick = (type: string, action: string) => {
    if (action === 'view') {
      const uploadedFile = uploadedFiles[type];
      if (uploadedFile && uploadedFile.location) {
        const fileUrl = `${global.documents_image_url}${uploadedFile.location}`;
        if (fileUrl) {
          window.open(fileUrl, '_blank'); 
        } else {
          toast.error('File URL is not available');
        }
      } else {
        toast.error('No location available for this document');
      }
    } else if (action === 'remove' && uploadedFiles[type]) {
      const id = uploadedFiles[type]?.id;
      if (id !== undefined) {
        handleOpenDeleteModal(id, type);
      }
    }
  };

  const renderFileInput = (label: string, type: string) => {
    const requiresDate = ['insurance', 'fitnessCertificate', 'registrationCertificate', 'pollutionCertificate'].includes(type);
    
    const hasUploadedDate = type === 'insurance' ? insuranceExpiryDate : 
                            type === 'fitnessCertificate' ? fitnessExpiryDate :
                            type === 'registrationCertificate' ? registrationDate :
                            type === 'pollutionCertificate' ? pollutionDate : null;

    const dateLabel = type === 'registrationCertificate' ? 'Registration Date:' :
                      type === 'pollutionCertificate' ? 'Pollution Date:' :
                      'Expiry Date:';

    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} width="100%">
        <Typography sx={{ fontSize: { xs: '15px', sm: '17px' }, fontWeight: 500 }}>
          {label}
        </Typography>
        <Box display="flex" alignItems="center">
          <input
            accept="*"
            style={{ display: 'none' }}
            id={`${type}-file`}
            type="file"
            onChange={(e) => handleFileChange(e, type)}
          />
          <label htmlFor={`${type}-file`}>
            <Tooltip title="Upload File">
              <IconButton 
                color="primary" 
                component="span"
                onClick={(e) => {
                  if (requiresDate && icons[type] === 'upload') {
                    e.preventDefault();
                    triggerUploadFlow(type);
                  }
                }}
              >
                {icons[type] === 'upload' && <UploadIcon />}
              </IconButton>
            </Tooltip>
          </label>
          {icons[type] === 'view' && (
            <>
              <Tooltip title="View File">
               <IconButton sx={{ color: 'black' }} onClick={() => handleIconClick(type, 'view')}>
                                   <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove File">
                 <Tooltip title="Remove File">
                  <IconButton sx={{ color: 'red' }} onClick={() => handleIconClick(type, 'remove')}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Tooltip>
            </>
          )}
        </Box>
        
        {requiresDate && hasUploadedDate && icons[type] === 'view' && (
          <Box sx={{ width: '100%', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {dateLabel} {hasUploadedDate}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog 
      open={isDocumentOpen} 
      onClose={handlePopUpClose} 
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: { xs: '95%', sm: '700px' },
          maxWidth: '100%',
          margin: { xs: 2, sm: 'auto' }, 
          maxHeight: { xs: '90vh', sm: 'auto' }
        }
      }}
    >
         <Box
        sx={{
          marginLeft: {
            xs: '0px',
            sm: '-180px',
          },
        }}
      >              
        <Heading title="Add Document" closeIcon="true" onClose={handlePopUpClose} />
      </Box>
     
      <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: 2 }}>
        <Box sx={{ border: '2px solid #e0e0e0', borderRadius: 2, p: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 4 }}>
            
            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              {renderFileInput('Registration Certificate (RC)', 'registrationCertificate')}
            </Grid>
            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              {renderFileInput('Pollution Certificate', 'pollutionCertificate')}
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ borderBottom: '1px solid #e0e0e0', my: 1 }} />
            </Grid>

            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              {renderFileInput('Insurance', 'insurance')}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ borderBottom: '1px solid #e0e0e0', my: 1 }} />
            </Grid>

            <Grid item xs={12} sm={6} display="flex" alignItems="center">
              {renderFileInput('Fitness Certificate (FC)', 'fitnessCertificate')}
            </Grid>

          </Grid>
        </Box>

        <ConfirmationPopup
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Confirm Deletion"
          message={`Are you sure you want to delete this ${docTypeToDelete?.replace('_', ' ')}?`}
        />

        <DocumentUploadDialog
          isOpen={uploadFlow.isOpen}
          docType={uploadFlow.docType}
          onDateSelected={handleDateSelected}
          onClose={() => setUploadFlow(prev => ({ ...prev, isOpen: false }))}
          requiresDate={true}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddDocumentModal;